// src/pages/admin/AdminUsersPage.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminUsers, useExportUsers } from '../hooks';
import { UserTable } from '../components';
import { useAdminStore } from '@/features/admin/stores/adminStore';
import { UserRole, UserStatus } from '@/types/admin';

export const AdminUsersPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const { userFilters, setUserFilters, selectedItems, toggleSelectedItem, clearSelectedItems } = useAdminStore();
  
  const { data, isLoading } = useAdminUsers({
    page,
    limit: 20,
    search: userFilters.search,
    role: userFilters.role,
    status: userFilters.status,
    sort_by: userFilters.sortBy,
    sort_order: userFilters.sortOrder,
  });

  const exportMutation = useExportUsers();

  const handleExport = () => {
    exportMutation.mutate({ format: 'csv' });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold text-navy">User Management</h1>
          <p className="text-warm-gray">Manage platform users and permissions</p>
        </div>
        <button
          onClick={handleExport}
          disabled={exportMutation.isPending}
          className="px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
        >
          Export Users
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          <input
            type="text"
            placeholder="Search users..."
            value={userFilters.search || ''}
            onChange={(e) => setUserFilters({ search: e.target.value })}
            className="flex-1 min-w-[200px] px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/20"
          />
          <select
            value={userFilters.role || ''}
            onChange={(e) => setUserFilters({ role: e.target.value as UserRole || undefined })}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/20"
          >
            <option value="">All Roles</option>
            <option value="family_member">Family Member</option>
            <option value="caregiver">Caregiver</option>
            <option value="attorney">Attorney</option>
            <option value="vendor">Vendor</option>
            <option value="provider">Provider</option>
            <option value="mentor">Mentor</option>
            <option value="admin">Admin</option>
          </select>
          <select
            value={userFilters.status || ''}
            onChange={(e) => setUserFilters({ status: e.target.value as UserStatus || undefined })}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/20"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending</option>
            <option value="deactivated">Deactivated</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <UserTable
        users={data?.items || []}
        isLoading={isLoading}
        selectedIds={selectedItems}
        onSelect={toggleSelectedItem}
        onSelectAll={(ids) => ids.forEach(toggleSelectedItem)}
      />

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-warm-gray">
            Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, data.total)} of {data.total} users
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-200 rounded-lg disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(data.total_pages, p + 1))}
              disabled={page === data.total_pages}
              className="px-3 py-1 border border-gray-200 rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;



